import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { MaskedLines } from "@/lib/motion";
import { BRAND, IMAGES } from "@/data/urban";
import { HERO } from "@/constants/testIds";
import { InquiryForm } from "@/components/ur/InquiryForm";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);

  return (
    <section id="home" ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden bg-black">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={IMAGES.livingHero} alt="Modern Indian home interior by Urban Renovations" className="h-full w-full object-cover opacity-70" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-28 pb-16 w-full">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-4 py-1.5 text-white/90 overline">
              <span className="h-1.5 w-1.5 rounded-full bg-white" /> Urban Painter · Working Since 2019
            </motion.div>

            <h1 className="mt-6 font-heading text-white font-extrabold leading-[0.98] tracking-tight text-5xl sm:text-6xl lg:text-[5.4rem]">
              <MaskedLines lines={["Beautiful Homes."]} />
              <MaskedLines lines={["Smart Budgets."]} delay={0.12} />
            </h1>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-white/80 leading-relaxed">
              Budget-friendly interior and renovation solutions designed around your space, lifestyle and budget.
              Modular kitchens from <span className="text-white font-semibold">₹1,200/sq.ft.</span>
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/contact" data-testid={HERO.quoteBtn} className="inline-flex items-center gap-2 bg-white text-black px-7 py-3.5 rounded-md font-bold uppercase tracking-wider text-sm hover:bg-white/90 transition-colors">
                Get Free Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/designs" data-testid={HERO.visitBtn} className="inline-flex items-center gap-2 border border-white/40 text-white px-7 py-3.5 rounded-md font-bold uppercase tracking-wider text-sm hover:bg-white hover:text-black transition-colors">
                Explore Designs
              </Link>
            </motion.div>

            <div className="mt-8 text-white/70 text-sm">Call now: <a href={`tel:${BRAND.phoneRaw}`} className="text-white font-semibold underline underline-offset-4">{BRAND.phone}</a></div>
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}
            className="lg:col-span-5">
            <InquiryForm heading="Tell Us About Your Home" subheading="Free consultation · Reply within 24 hrs" source="hero" testId="hero-form" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
